<template>
  <div class="a-provenance-item">
    <div ref="timespan" class="a-provenance-item__timespan">
      {{ timespan }}
    </div>
    <div class="a-provenance-item__details">
      <p v-if="place" ref="place" class="a-provenance-item__place">
        {{ placeFoundText }}
      </p>
      <p
        v-for="owner in owners"
        :key="owner.id"
        ref="owner_name"
        class="a-provenance-item__owner"
      >
        <span v-if="owner.prefix" ref="prefix" class="a-provenance-item__prefix"
          >{{ owner.prefix }}&hairsp;
        </span>
        <LinkOrText
          ref="link"
          :route="suppressOwner(owner) ? undefined : owner.to"
          :title="owner.name"
          class="a-provenance-item__owner_name"
        />
        <span v-if="owner.suffix" ref="suffix" class="a-provenance-item__suffix"
          >{{ owner.suffix }}
        </span>
        <span
          v-if="owner.lugtNumber"
          ref="lugt_number"
          class="a-provenance-item__lugt_number"
          >{{ getLugtNumberText(owner) }}</span
        >
        <span
          v-if="getConsituentDetails(owner)"
          ref="constituent_details"
          class="a-provenance-item__constituent_details"
          >{{ getConsituentDetails(owner) }}</span
        >
        <span
          v-if="location"
          ref="location"
          class="a-provenance-item__location"
        >
          {{ locationText }}
        </span>
      </p>
      <p v-if="statement" ref="statement" class="a-provenance-item__statement">
        {{ statement }}
      </p>
      <p v-if="saleNote" ref="saleNote" class="a-provenance-item__sale-note">
        {{ saleNoteText }}
      </p>
      <p v-if="citation" ref="citation" class="a-provenance-item__citation">
        {{ citationText }}
      </p>
      <span v-if="remarks" ref="remarks" class="a-provenance-item__remarks">
        {{ remarksText }}
      </span>
    </div>
  </div>
</template>

<script>
import LinkOrText from "@/components/atoms/linkOrText/index.vue";
import { useLocalization } from "@thegetty-private/getty-ui";

export default {
  name: "ProvenanceItem",
  components: { LinkOrText },

  props: {
    /**
     * The Citation for the provenance text (optional)
     */
    citation: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The Owner object
     */
    owners: {
      type: Array,
      required: false,
      default: () => {
        return [];
      }
    },
    /**
     * The Place (if there's no owner)
     */
    place: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The Location of the sale
     */
    location: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The remarks/note (optional)
     */
    remarks: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The sale note (optional)
     */
    saleNote: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The descriptive statement of the sale
     */
    statement: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The timespan for the sale
     */
    timespan: {
      type: String,
      required: false,
      default: "-"
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },

  computed: {
    /** Gets the properly formatted citation string to display
     *
     * @returns {string}
     */
    citationText() {
      return this.translatePart("provenance_list.citation", {
        citation: this.citation
      });
    },
    /**
     * Gets the Owner name
     *
     * @returns {string}
     */
    constituentName() {
      return this.owner.name;
    },
    /**
     * Gets the properly formatted location string to display
     *
     * @returns {string}
     */
    locationText() {
      return this.translatePart("provenance_list.location", {
        location: this.location
      });
    },
    /**
     * Gets the properly formatted place found string to display
     *
     * @returns {string}
     */
    placeFoundText() {
      return this.translatePart("provenance_list.place_found", {
        place: this.place
      });
    },
    /** Gets the properly formatted remarks string to display
     *
     * @returns {string}
     */
    remarksText() {
      if (this.place) {
        return this.remarks;
      }
      return this.translatePart("provenance_list.remarks", {
        remarks: this.remarks
      });
    },
    /** Gets the properly formatted sale note string to display
     *
     * @returns {string}
     */
    saleNoteText() {
      return this.translatePart("provenance_list.sale_note", {
        sale_note: this.saleNote
      });
    }
  },
  methods: {
    /**
     * Get the display text from the constituent details component
     *
     * @returns {string}
     */
    getConsituentDetails(owner) {
      if (owner.nationality_and_dates) {
        return owner.nationality_and_dates;
      }

      if (owner.life_dates) {
        return owner.life_dates;
      }
      return undefined;
    },
    /**
     * Gets the properly formatted lugt number to display
     *
     * @returns {string}
     */
    getLugtNumberText(owner) {
      return this.translatePart("provenance_list.lugt_number", {
        lugt_number: owner.lugtNumber.join(", ")
      });
    },
    /**
     * Whether we should show the link to the owner or not
     *
     * @returns {boolean}
     */
    suppressOwner(owner) {
      // suppress things with private or unknwon
      return owner && owner.hideLink == true;
    }
  }
};
</script>
<style lang="scss">
@import "./index.scss";
</style>
